import { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { useOutsideClick } from '../hooks/useOutsideClick';

type Position = { x: number; y: number };

type MenusContextValue = {
  openId: string;
  position: Position | null;
  open: (id: string) => void;
  close: () => void;
  setPosition: (pos: Position) => void;
};

const MenusContext = createContext<MenusContextValue | null>(null);

function useMenusContext() {
  const ctx = useContext(MenusContext);
  if (!ctx)
    throw new Error('Menus compound components must be used within <Menus>');
  return ctx;
}

function Menus({ children }: { children: React.ReactNode }) {
  const [openId, setOpenId] = useState('');
  const [position, setPosition] = useState<Position | null>(null);

  const close = () => setOpenId('');
  const open = setOpenId;

  return (
    <MenusContext.Provider
      value={{ openId, close, open, position, setPosition }}
    >
      {children}
    </MenusContext.Provider>
  );
}

function Toggle({
  id,
  children,
  className,
}: {
  id: string;
  children?: React.ReactNode;
  className?: string;
}) {
  const { openId, close, open, setPosition } = useMenusContext();

  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      x: window.innerWidth - rect.width - rect.x,
      y: rect.y + rect.height + 8,
    });
    openId === '' || openId !== id ? open(id) : close();
  }

  return (
    <button
      onClick={handleClick}
      className={
        className ??
        'hover:bg-bgSecondary flex items-center justify-center rounded-md transition-all'
      }
    >
      {children ?? <MoreVertical className="h-6 w-6" />}
    </button>
  );
}

function List({
  id,
  children,
  className,
}: {
  id: string;
  children: React.ReactNode;
  className?: string;
}) {
  const { openId, position, close } = useMenusContext();
  const ref = useOutsideClick<HTMLUListElement>({ handler: close });

  if (openId !== id) return null;

  return createPortal(
    <ul
      ref={ref}
      className={
        className ??
        'bg-surface border-surface-muted fixed z-50 min-w-40 rounded-md border p-1 shadow-md'
      }
      style={{ right: `${position?.x}px`, top: `${position?.y}px` }}
    >
      {children}
    </ul>,
    document.body,
  );
}

function Button({
  children,
  icon,
  onClick,
  className,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  const { close } = useMenusContext();

  function handleClick() {
    onClick?.();
    close();
  }

  return (
    <li>
      <button
        onClick={handleClick}
        className={
          className ??
          'text-ink-dim hover:bg-surface-elevated hover:text-surface-light flex w-full cursor-pointer items-center gap-3 rounded-sm px-4 py-2 text-left text-sm transition-colors'
        }
      >
        {icon}
        <span>{children}</span>
      </button>
    </li>
  );
}

Menus.Toggle = Toggle;
Menus.List = List;
Menus.Button = Button;

export default Menus;
